import api from './api';
import { getPreferredLanguage, getBackendLanguageId } from '../constants/Languages';

/**
 * AuthService handles all authentication-related API calls.
 * Follows SOLID principles: single responsibility, open for extension.
 */
const AuthService = {
  /**
   * Login as team member.
   * @param {string} email
   * @param {string} password
   * @returns {Promise<Object>} Resolves with user data on success, rejects on error.
   */
  loginTeamMember: async (email, password) => {
    const response = await api.post('/auth/team-member/login', { email, password });
    // Store the token and user data
    if (response.data.accessToken) {
      localStorage.setItem('management_auth_token', response.data.accessToken);
      if (response.data.refreshToken) {
        localStorage.setItem('management_refresh_token', response.data.refreshToken);
      }
    }
    
    return response;
  },

  /**
   * Login as app admin.
   * @param {string} email
   * @param {string} password
   * @returns {Promise<Object>} Resolves with user data on success, rejects on error.
   */
  loginAppAdmin: async (email, password) => {
    const response = await api.post('/auth/admin/login', { email, password });
    // Store the token and user data
    if (response.data.accessToken) {
      localStorage.setItem('management_auth_token', response.data.accessToken);
      if (response.data.refreshToken) {
        localStorage.setItem('management_refresh_token', response.data.refreshToken);
      }
    }
    
    return response;
  },

  /**
   * Check whether a customer account exists for the login flow.
   * @param {string} email
   * @returns {Promise<{ exists: boolean, hasPassword: boolean }>}
   */
  checkCustomerLoginEmail: async (email) => {
    const response = await api.get('/auth/customer/email-check', {
      params: { email },
    });
    return response.data;
  },

  /**
   * Login as customer.
   * @param {string} email
   * @param {string} password
   * @returns {Promise<Object>} Resolves with user data on success, rejects on error.
   */
  loginCustomer: async (email, password) => {
    const response = await api.post('/auth/customer/login', { email, password });
    // Store the token and user data
    if (response.data.accessToken) {
      localStorage.setItem('customer_auth_token', response.data.accessToken);
      if (response.data.refreshToken) {
        localStorage.setItem('customer_refresh_token', response.data.refreshToken);
      }
    }
    
    return response;
  },

  /**
   * Request password reset for team member.
   * @param {string} email
   * @returns {Promise<Object>} Resolves on success, rejects on error.
   */
  requestPasswordResetTeamMember: async (email) => {
    return await api.post('/auth/request-password-reset', { 
      email, 
      userType: 'TeamMember' 
    });
  },

  /**
   * Request password reset for app admin.
   * @param {string} email
   * @returns {Promise<Object>} Resolves on success, rejects on error.
   */
  requestPasswordResetAppAdmin: async (email) => {
    return await api.post('/auth/request-password-reset', { 
      email, 
      userType: 'AppAdmin' 
    });
  },

  /**
   * Request password set for team member.
   * @param {string} email
   * @returns {Promise<Object>} Resolves on success, rejects on error.
   */
  requestPasswordSetTeamMember: async (email) => {
    return await api.post('/auth/request-password-set', { 
      email, 
      userType: 'TeamMember' 
    });
  },

  /**
   * Request password set for app admin.
   * @param {string} email
   * @returns {Promise<Object>} Resolves on success, rejects on error.
   */
  requestPasswordSetAppAdmin: async (email) => {
    return await api.post('/auth/request-password-set', { 
      email, 
      userType: 'AppAdmin' 
    });
  },

  /**
   * Reset password for team member.
   * @param {string} email
   * @param {string} token
   * @param {string} newPassword
   * @returns {Promise<Object>} Resolves on success, rejects on error.
   */
  resetPasswordTeamMember: async (email, token, newPassword) => {
    return await api.post('/auth/reset-password', { 
      email, 
      userType: 'TeamMember',
      token, 
      newPassword 
    });
  },

  /**
   * Reset password for app admin.
   * @param {string} email
   * @param {string} token
   * @param {string} newPassword
   * @returns {Promise<Object>} Resolves on success, rejects on error.
   */
  resetPasswordAppAdmin: async (email, token, newPassword) => {
    return await api.post('/auth/reset-password', { 
      email, 
      userType: 'AppAdmin',
      token, 
      newPassword 
    });
  },

  /**
   * Set password for team member.
   * @param {string} email
   * @param {string} token
   * @param {string} newPassword
   * @returns {Promise<Object>} Resolves on success, rejects on error.
   */
  setPasswordTeamMember: async (email, token, newPassword) => {
    return await api.post('/auth/set-password', { 
      email, 
      userType: 'TeamMember',
      token, 
      newPassword 
    });
  },

  /**
   * Set password for app admin.
   * @param {string} email
   * @param {string} token
   * @param {string} newPassword
   * @returns {Promise<Object>} Resolves on success, rejects on error.
   */
  setPasswordAppAdmin: async (email, token, newPassword) => {
    return await api.post('/auth/set-password', { 
      email, 
      userType: 'AppAdmin',
      token, 
      newPassword 
    });
  },

  /**
   * Request password reset for customer.
   * @param {string} email
   * @returns {Promise<Object>} Resolves on success, rejects on error.
   */
  requestPasswordResetCustomer: async (email) => {
    return await api.post('/auth/request-password-reset', { 
      email, 
      userType: 'Customer' 
    });
  },

  /**
   * Request password set for customer.
   * @param {string} email
   * @returns {Promise<Object>} Resolves on success, rejects on error.
   */
  requestPasswordSetCustomer: async (email, storeId) => {
    return await api.post('/auth/request-password-set', { 
      email, 
      userType: 'Customer',
      storeId: storeId || undefined,
    });
  },

  /**
   * Reset password for customer.
   * @param {string} email
   * @param {string} token
   * @param {string} newPassword
   * @returns {Promise<Object>} Resolves on success, rejects on error.
   */
  resetPasswordCustomer: async (email, token, newPassword) => {
    return await api.post('/auth/reset-password', { 
      email, 
      userType: 'Customer',
      token, 
      newPassword 
    });
  },

  /**
   * Set password for customer.
   * @param {string} email
   * @param {string} token
   * @param {string} newPassword
   * @returns {Promise<Object>} Resolves on success, rejects on error.
   */
  setPasswordCustomer: async (email, token, newPassword) => {
    return await api.post('/auth/set-password', { 
      email, 
      userType: 'Customer',
      token, 
      newPassword 
    });
  },

  /**
   * Login with Google for customer.
   * @param {string} idToken - Google ID token
   * @param {string} language - Language code (pt, en, es) - optional, defaults to stored preference
   * @returns {Promise<Object>} Resolves with user data on success, rejects on error.
   */
  loginWithGoogle: async (idToken, language = null) => {
    // If language is already a number (enum value), use it directly
    // Otherwise, get from localStorage and convert
    let backendLanguage;
    if (typeof language === 'number') {
      backendLanguage = language;
    } else {
      backendLanguage = getBackendLanguageId(getPreferredLanguage());
    }
    
    const response = await api.post('/auth/customer/login/google', { 
      idToken,
      language: backendLanguage
    });
    if (response.data.accessToken) {
      localStorage.setItem('customer_auth_token', response.data.accessToken);
      if (response.data.refreshToken) {
        localStorage.setItem('customer_refresh_token', response.data.refreshToken);
      }
    }
    return response;
  },

  /**
   * Register a new customer.
   * @param {Object} customerData - Customer registration data
   * @returns {Promise<Object>} Resolves with user data on success, rejects on error.
   */
  registerCustomer: async (customerData) => {
    const language =
      typeof customerData?.language === 'number'
        ? customerData.language
        : getBackendLanguageId(customerData?.language ?? getPreferredLanguage());

    const response = await api.post('/auth/customer/register', {
      ...customerData,
      language,
    });
    if (response.data.accessToken) {
      localStorage.setItem('customer_auth_token', response.data.accessToken);
      if (response.data.refreshToken) {
        localStorage.setItem('customer_refresh_token', response.data.refreshToken);
      }
    }
    return response;
  },

  requestCustomerPhoneClaimOtp: async ({ phone, language, storeId }) => {
    const response = await api.post('/auth/customer/claim/request-otp', {
      phone,
      language,
      storeId: storeId || undefined,
    });
    return response.data;
  },

  verifyCustomerPhoneClaimOtp: async ({ phone, code }) => {
    const response = await api.post('/auth/customer/claim/verify-otp', {
      phone,
      code,
    });
    return response.data;
  },

  completeCustomerPhoneClaim: async ({ claimToken, email, storeId }) => {
    const response = await api.post('/auth/customer/claim/complete', {
      claimToken,
      email,
      storeId: storeId || undefined,
    });
    return response.data;
  },
};

export default AuthService;